import * as React from 'react';
import styled from 'styled-components';

import { isPermitted } from 'util/PermissionsMixin';
import { TD, TH, RowTitle } from 'security-app/components/common';
import useHistory from 'routing/useHistory';
import Routes from 'routing/Routes';
import type { InvestigationsDetailsAPIType, LogEvidenceType } from 'security-app/hooks/api/investigationsAPI.types';
import useCurrentUser from 'hooks/useCurrentUser';
import BootstrapModalConfirm from 'components/bootstrap/BootstrapModalConfirm';
import { Button, Label, Table } from 'components/bootstrap';
import { DataWell } from 'common/components';
import { useRemoveEvidence } from 'security-app/hooks/useInvestigationsAPI';
import { Icon } from 'components/common';

import LogsModal from './LogsModal';

import { IconButton } from '../styled.components';

const StyledLabel = styled(Label)`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin-left: 0.5rem;
`;

const StyledCollapsableSectionHeading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

type Props = {
  investigation: InvestigationsDetailsAPIType;
  isExpanded: boolean;
  onToggleExpanded: (sectionName: string) => void;
};

function Logs({ investigation, isExpanded, onToggleExpanded }: Props) {
  const { permissions } = useCurrentUser();
  const history = useHistory();
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
  const [deleteLog, setDeleteLog] = React.useState<LogEvidenceType>(null);

  const canManageEvidence = React.useMemo(
    () => !investigation.archived && isPermitted(permissions, 'investigations:edit'),
    [investigation.archived, permissions],
  );

  const { removeEvidence, removingEvidence } = useRemoveEvidence();

  const removeLog = async () => {
    await removeEvidence({
      invId: investigation.id,
      payload: {
        logs: [{ id: deleteLog.id, index: deleteLog.index }],
      },
    });

    setShowDeleteDialog(false);
    setDeleteLog(null);
  };

  const onDelete = (log: LogEvidenceType) => (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
    setDeleteLog(log);
  };

  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteLog(null);
  };

  const renderLogs = (logs) => (
    <>
      {logs.map((log: LogEvidenceType) => (
        <tr key={log.id}>
          <TD data-testid="log-name">
            <RowTitle onClick={() => history.push(Routes.message_show(log.index, log.id))}>{log.id}</RowTitle>
          </TD>
          <TD>{log.index}</TD>
          {canManageEvidence && (
            <TD>
              <IconButton $danger data-testid={`delete-${log.id}`} onClick={onDelete(log)}>
                <Icon name="close" />
              </IconButton>
            </TD>
          )}
        </tr>
      ))}
    </>
  );

  return (
    <DataWell $color="content">
      <StyledCollapsableSectionHeading>
        <IconButton data-testid="toggle-logs" onClick={() => onToggleExpanded('logs')}>
          {isExpanded ? <Icon name="keyboard_arrow_up" /> : <Icon name="keyboard_arrow_down" />}
          <h2 style={{ marginLeft: '1rem' }}>Logs</h2>
          <StyledLabel>{investigation.logs.length}</StyledLabel>
        </IconButton>
        {canManageEvidence && (
          <Button data-testid="add-log" onClick={() => setShowAddModal(true)}>
            <Icon name="add" size="sm" />
            Add Logs
          </Button>
        )}
      </StyledCollapsableSectionHeading>
      {isExpanded && (
        <div style={{ marginTop: '0.5rem' }}>
          {investigation.logs.length === 0 ? (
            <div style={{ color: 'gray' }}>There are no logs to list</div>
          ) : (
            <Table condensed hover className="table table-striped" data-testid="logs-list">
              <thead>
                <tr>
                  <TH>Log ID</TH>
                  <TH>Log Index</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>{renderLogs(investigation.logs)}</tbody>
            </Table>
          )}
        </div>
      )}
      <LogsModal show={showAddModal} onCancel={() => setShowAddModal(false)} />
      <BootstrapModalConfirm
        showModal={showDeleteDialog}
        title="Delete Log"
        onConfirm={removeLog}
        onCancel={onCancelDelete}
        confirmButtonDisabled={removingEvidence}>
        Are you sure you want to delete this Log?
      </BootstrapModalConfirm>
    </DataWell>
  );
}

export default Logs;
